import { AccredibleObjectWithId } from '@accredible-frontend-v2/models';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { DirectoryStoreActions } from '../../../../../../core/stores/directory/directory.actions';
import { DirectoryState } from '../../../../../../core/stores/directory/directory.reducer';
import {
  selectDirectoryId,
  selectDirectoryOrganizations,
} from '../../../../../../core/stores/directory/directory.selectors';
import { FilterService } from '../../../../services/filter.service';
import {
  SpotlightProfilesState,
  SpotlightProfilesStateAction,
} from '../../../../store/spotlight-profiles/spotlight-profiles.reducer';
import {
  selectSpotlightProfilesFiltersOrganizations,
  selectSpotlightProfilesStateAction,
} from '../../../../store/spotlight-profiles/spotlight-profiles.selectors';

@Component({
  selector: 'sl-profile-list-issuer-filter',
  templateUrl: './profile-list-issuer-filter.component.html',
  styleUrls: ['./profile-list-issuer-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileListIssuerFilterComponent implements OnInit {
  private readonly _filter = inject(FilterService);
  private readonly _spotlightProfileStore = inject(Store<SpotlightProfilesState>);
  private readonly _directoryStore = inject(Store<DirectoryState>);
  private readonly _destroyRef = inject(DestroyRef);

  issuers$ = this._directoryStore.select(selectDirectoryOrganizations);
  selectedIssuers$ = this._spotlightProfileStore.select(
    selectSpotlightProfilesFiltersOrganizations,
  );

  selectedIssuers = signal<AccredibleObjectWithId[]>([]);

  ngOnInit(): void {
    this._setInitialSelectedIssuers();
    this._setupFiltersResetListener();
  }

  onSelectedIssuersChange(issuers: AccredibleObjectWithId[]): void {
    this._filter.updateRouterQueryParams({ organizations: issuers.map((group) => group.id) });
    this._loadEligibleGroups(issuers.map((issuer) => Number(issuer.id)));
  }

  private _setInitialSelectedIssuers(): void {
    combineLatest([this.selectedIssuers$, this.issuers$])
      .pipe(
        take(1),
        map(([selectedIssuers, issuers]) =>
          issuers.filter((issuer: AccredibleObjectWithId) =>
            selectedIssuers?.includes(Number(issuer.id)),
          ),
        ),
      )
      .subscribe((selectedIssuers) => {
        this.selectedIssuers.set(selectedIssuers);
      });
  }

  private _loadEligibleGroups(organizationIds: number[]): void {
    this._directoryStore
      .select(selectDirectoryId)
      .pipe(take(1))
      .subscribe((directoryId) => {
        this._directoryStore.dispatch(
          DirectoryStoreActions.loadEligibleGroups({
            directoryId,
            organizationIds,
          }),
        );
      });
  }

  private _setupFiltersResetListener(): void {
    this._spotlightProfileStore
      .select(selectSpotlightProfilesStateAction)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((action) => action === SpotlightProfilesStateAction.FILTERS_RESET),
      )
      .subscribe(() => {
        this.selectedIssuers.set([]);
      });
  }
}
